// src/messaging/admin/ui.jsx
// 先生・教務・運営者向けのメッセージ画面の小さな部品【2026-10-16】
import React, { useEffect, useRef, useState } from 'react';
import { msgApi } from '../api';
import { fmtBytes, scheduleBounds, prepareAttachment } from './util';
import './admin.css';
import BaseNotice from '../../components/Notice';

/** お知らせ欄。kind: info / ok / warn / error
 *  【2026-11-02】見た目はほかの画面とそろえて components/Notice.jsx に任せる（ok は success）。
 *  ここでは今までどおりその場に出す（トーストにはしない）。onClose があれば × で親の状態も消す */
export function Notice({ kind = 'info', children, onClose }) {
  if (!children) return null;
  return <BaseNotice kind={kind === 'ok' ? 'success' : kind} onClose={onClose}>{children}</BaseNotice>;
}

export function Loading({ text = '読み込み中...' }) {
  return <div className="ma-muted ma-pad">{text}</div>;
}

export function Empty({ children }) {
  return <div className="ma-empty">{children}</div>;
}

/** 小さな札。tone: gray / blue / green / amber / red */
export function Tag({ tone = 'gray', children, title }) {
  return <span className={'ma-tag ma-tag--' + tone} title={title}>{children}</span>;
}

/** 文字数（上限に近いと色が変わる） */
export function CharCount({ value, max }) {
  const n = (value || '').length;
  const cls = n > max ? 'ma-count ma-count--over' : n > max * 0.9 ? 'ma-count ma-count--near' : 'ma-count';
  return <span className={cls}>{n} / {max}</span>;
}

/** 既読のバー（n / m 人） */
export function ReadBar({ read, total }) {
  const t = Number(total) || 0;
  const r = Math.min(Number(read) || 0, t || Number(read) || 0);
  const pct = t > 0 ? Math.round((r / t) * 100) : 0;
  return (
    <div className="ma-readbar">
      <span className="ma-readbar-label">既読 <strong>{r}</strong> / {t}人</span>
      <span className="ma-readbar-track" aria-hidden="true">
        <span className="ma-readbar-fill" style={{ width: pct + '%' }} />
      </span>
    </div>
  );
}

/** 画像（Bearer が要るので blob にして出す） */
export function AuthImage({ url, alt = '画像', className = 'ma-thumb' }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setSrc(null); setFailed(false);
    msgApi.blobUrl(url).then(u => {
      if (!alive) return;
      if (u) setSrc(u); else setFailed(true);
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [url]);
  if (failed) return <span className="ma-thumb-missing">画像を表示できませんでした</span>;
  if (!src) return <span className="ma-thumb-loading">画像を読み込み中...</span>;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="ma-thumb-link">
      <img src={src} alt={alt} className={className} />
    </a>
  );
}

/** 添付（画像・PDF）を出す。API の { image, file } をそのまま渡す */
export function AttachmentView({ image, file }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (image && image.url) return <div className="ma-attach"><AuthImage url={image.url} /></div>;
  if (file && file.url) {
    const save = async () => {
      setBusy(true); setErr('');
      const ok = await msgApi.download(file.url, file.name || 'file.pdf');
      setBusy(false);
      if (!ok) setErr('ファイルを取り出せませんでした');
    };
    return (
      <div className="ma-attach">
        <button type="button" className="ma-file" onClick={save} disabled={busy}>
          <span className="ma-file-icon" aria-hidden="true">PDF</span>
          <span className="ma-file-name">{file.name || 'file.pdf'}</span>
          {file.size != null && <span className="ma-file-size">{fmtBytes(file.size)}</span>}
          <span className="ma-file-act">{busy ? '保存中...' : '保存'}</span>
        </button>
        {err && <div className="ma-err-inline">{err}</div>}
      </div>
    );
  }
  return null;
}

/**
 * 予約の欄。「すぐ送る / 日時を決めて送る」
 *   value: '' のときすぐ送る。'YYYY-MM-DDTHH:mm'（日本時間）のとき予約
 */
export function ScheduleField({ enabled, onToggle, value, onChange, idPrefix = 'sch' }) {
  return (
    <div className="ma-schedule">
      <label className="ma-check">
        <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} id={idPrefix + '-on'} />
        日時を決めて送る（予約）
      </label>
      {enabled && <ScheduleInput value={value} onChange={onChange} />}
    </div>
  );
}

/** 送る日時の入力欄だけ（予約を直すとき） */
export function ScheduleInput({ value, onChange }) {
  const b = scheduleBounds();
  return (
    <div className="ma-schedule-row">
      <input
        type="datetime-local"
        className="ma-input"
        value={value}
        min={b.min}
        max={b.max}
        onChange={e => onChange(e.target.value)}
        aria-label="送る日時"
      />
      <span className="ma-hint">日本時間・60日先まで</span>
    </div>
  );
}

/** 画像・PDF を1つ選ぶ欄。選んだら縮めてから onChange(file) */
export function AttachmentPicker({ file, onChange, onError, disabled }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (file && file.type && file.type.startsWith('image/')) {
      const u = URL.createObjectURL(file);
      setPreview(u);
      return () => URL.revokeObjectURL(u);
    }
    setPreview(null);
    return undefined;
  }, [file]);

  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setWorking(true);
    const r = await prepareAttachment(f);
    setWorking(false);
    if (r.error) { onError && onError(r.error); return; }
    onChange(r.file);
  };

  return (
    <div className="ma-picker">
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={pick}
        style={{ display: 'none' }}
      />
      {!file ? (
        <button type="button" className="btn-secondary" onClick={() => inputRef.current && inputRef.current.click()} disabled={disabled || working}>
          {working ? '画像を整えています...' : '画像・PDF を付ける'}
        </button>
      ) : (
        <div className="ma-picked">
          {preview
            ? <img src={preview} alt="付ける画像" className="ma-thumb ma-thumb--small" />
            : <span className="ma-file-icon" aria-hidden="true">PDF</span>}
          <span className="ma-picked-name">{file.name}<span className="ma-hint">（{fmtBytes(file.size)}）</span></span>
          <button type="button" className="btn-link" onClick={() => onChange(null)} disabled={disabled}>外す</button>
        </div>
      )}
      <div className="ma-hint">画像は自動で小さくしてから送ります。PDF は 10MB まで。</div>
    </div>
  );
}

/** ページの枠（見出し＋本文） */
export function Page({ title, lead, children, wide }) {
  return (
    <div className={'page-container ma-page' + (wide ? ' ma-page--wide' : '')}>
      <div className="page-header">
        <h1>{title}</h1>
        {lead && <p>{lead}</p>}
      </div>
      {children}
    </div>
  );
}
